import React, { useState } from 'react';
import { Routes, Route, Link, Navigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import CourseList from './CourseList';
import CourseForm from './CourseForm';
import UserList from './UserList';
import SchoolFinancialLedger from '../../components/admin/SchoolFinancialLedger';

import FeeManagement from './FeeManagement';
import PaymentRecording from './PaymentRecording';
import CurriculumRegistry from './CurriculumRegistry';
import ClubList from './ClubList';
import EventList from './EventList';
import { useApi } from '../../hooks/useApi';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import UserRegistrationModal from '../../components/admin/UserRegistrationModal';
import FeeFrameworkModal from '../../components/admin/FeeFrameworkModal';
import AcademicCycleModal from '../../components/admin/AcademicCycleModal';
import ClubModal from '../../components/admin/ClubModal';
import EventNoticeModal from '../../components/admin/EventNoticeModal';
import GradeManagement from './GradeManagement';
import SOWTrackerTab from '../../components/teacher/SOWTrackerTab';
import Household from './Household';
import AdminEnquiryManager from './AdminEnquiryManager';
import CurriculumAreaManager from './CurriculumAreaManager';
import HouseList from './HouseList';
import PerformanceStandards from './PerformanceStandards';
import DisciplineCategoryManager from './DisciplineCategoryManager';



const StatCard = ({ title, value, icon, color, link }) => (
    <div className="bg-white rounded-3xl shadow-sm border border-slate-100 p-6 hover:shadow-lg transition-all group">
        <div className="flex items-center justify-between">
            <div>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">{title}</p>
                <p className="text-4xl font-black text-[#18216D] mt-2 group-hover:scale-105 transition-transform origin-left">{value}</p>
                {link && (
                    <Link to={link} className="text-[10px] font-black text-[#FFC425] uppercase tracking-widest mt-4 inline-block hover:text-[#18216D] transition-colors">
                        Explore Data →
                    </Link>
                )}
            </div>
            <div className={`w-16 h-16 rounded-2xl bg-slate-50 flex items-center justify-center text-3xl ${color} shadow-inner`}>
                {icon}
            </div>
        </div>
    </div>
);


const Dashboard = () => {
    const { get } = useApi();
    const queryClient = useQueryClient();
    const [showUserModal, setShowUserModal] = useState(false);
    const [showFeeModal, setShowFeeModal] = useState(false);
    const [showCycleModal, setShowCycleModal] = useState(false);
    const [showClubModal, setShowClubModal] = useState(false);
    const [showEventModal, setShowEventModal] = useState(false);

    const { data: stats = {
        totalLearningAreas: 0,
        totalStrands: 0,
        totalTeachers: 0,
        totalStudents: 0,
    }, isLoading: loading } = useQuery({
        queryKey: ['admin-stats'],
        queryFn: () => get('/api/admin/stats/'),
        staleTime: 5 * 60 * 1000,  // 5 minutes — stats don't change often
    });

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="py-20 text-center animate-pulse text-[10px] font-black text-slate-300 uppercase tracking-widest">Querying System Status...</div>
            </div>
        );
    }

    return (
        <div className="space-y-10">
            {/* Header */}
            <header className="relative bg-[#18216D] rounded-[2.5rem] p-10 text-white shadow-2xl shadow-indigo-900/10 overflow-hidden border border-white/5">
                <div className="absolute top-0 right-0 w-64 h-64 bg-[#FFC425]/10 rounded-full -mr-32 -mt-32 blur-3xl opacity-50"></div>
                <div className="relative z-10">
                    <h1 className="text-4xl font-black tracking-tighter uppercase italic">Administrative Hub</h1>
                    <p className="text-indigo-100/50 mt-1.5 font-bold text-[11px] uppercase tracking-widest">Kianda School Excellence <span className="mx-2 text-indigo-100/20">|</span> Unified Oversight Gateway</p>
                </div>
            </header>

            {/* Stats Grid */}
            <div className="flex md:grid md:grid-cols-2 lg:grid-cols-4 gap-6 overflow-x-auto pb-6 md:pb-0 scrollbar-hide snap-x">
                <div className="min-w-[85%] md:min-w-0 snap-center">
                    <StatCard
                        title="National Learning Areas"
                        value={stats.totalLearningAreas}
                        icon={<i className="fas fa-book-open"></i>}
                        color="text-[#18216D]"
                        link="/admin/curriculum"
                    />
                </div>
                <div className="min-w-[85%] md:min-w-0 snap-center">
                    <StatCard
                        title="Curriculum Strands"
                        value={stats.totalStrands}
                        icon={<i className="fas fa-layer-group"></i>}
                        color="text-[#FFC425]"
                        link="/admin/curriculum"
                    />
                </div>
                <div className="min-w-[85%] md:min-w-0 snap-center">
                    <StatCard
                        title="Teachers"
                        value={stats.totalTeachers}
                        icon={<i className="fas fa-chalkboard-user"></i>}
                        color="text-indigo-400"
                        link="/admin/users"
                    />
                </div>
                <div className="min-w-[85%] md:min-w-0 snap-center">
                    <StatCard
                        title="Students"
                        value={stats.totalStudents}
                        icon={<i className="fas fa-user-graduate"></i>}
                        color="text-slate-400"
                        link="/admin/users"
                    />
                </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-[2.5rem] shadow-sm border border-slate-50 p-8">
                <h2 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em] mb-8 flex items-center gap-3">
                    <span className="w-1.5 h-1.5 bg-[#FFC425] rounded-full"></span> Dispatch Controls
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <button
                        onClick={() => setShowUserModal(true)}
                        className="flex items-center space-x-6 p-6 bg-slate-50/50 border border-slate-100 rounded-[2rem] hover:border-[#18216D]/20 hover:bg-white hover:shadow-xl hover:shadow-indigo-900/5 transition-all group text-left"
                    >
                        <span className="h-14 w-14 rounded-2xl bg-indigo-50 text-[#18216D] flex items-center justify-center text-xl transition-transform group-hover:scale-110 shadow-sm border border-white">
                            <i className="fas fa-user-plus"></i>
                        </span>
                        <div>
                            <p className="font-extrabold text-[#18216D] uppercase text-xs tracking-widest">Manage Users</p>
                            <p className="text-[10px] font-bold text-slate-400 mt-1">Enroll Scholars, Faculty & Guardians</p>
                        </div>
                    </button>

                    <button
                        onClick={() => setShowFeeModal(true)}
                        className="flex items-center space-x-6 p-6 bg-slate-50/50 border border-slate-100 rounded-[2rem] hover:border-[#FFC425]/20 hover:bg-white hover:shadow-xl hover:shadow-amber-900/5 transition-all group text-left"
                    >
                        <span className="h-14 w-14 rounded-2xl bg-amber-50 text-[#FFC425] flex items-center justify-center text-xl transition-transform group-hover:scale-110 shadow-sm border border-white">
                            <i className="fas fa-file-invoice-dollar"></i>
                        </span>
                        <div>
                            <p className="font-extrabold text-[#18216D] uppercase text-xs tracking-widest">Manage Frameworks</p>
                            <p className="text-[10px] font-bold text-slate-400 mt-1">Establish New Fee Structures</p>
                        </div>
                    </button>

                    <button
                        onClick={() => setShowCycleModal(true)}
                        className="flex items-center space-x-6 p-6 bg-slate-50/50 border border-slate-100 rounded-[2rem] hover:border-emerald-500/20 hover:bg-white hover:shadow-xl hover:shadow-emerald-900/5 transition-all group text-left"
                    >
                        <span className="h-14 w-14 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center text-xl transition-transform group-hover:scale-110 shadow-sm border border-white">
                            <i className="fas fa-calendar-days"></i>
                        </span>
                        <div>
                            <p className="font-extrabold text-[#18216D] uppercase text-xs tracking-widest">Academic Cycle</p>
                            <p className="text-[10px] font-bold text-slate-400 mt-1">Manage Years & Termly Sessions</p>
                        </div>
                    </button>

                    <button
                        onClick={() => setShowClubModal(true)}
                        className="flex items-center space-x-6 p-6 bg-slate-50/50 border border-slate-100 rounded-[2rem] hover:border-indigo-500/20 hover:bg-white hover:shadow-xl hover:shadow-indigo-900/5 transition-all group text-left"
                    >
                        <span className="h-14 w-14 rounded-2xl bg-indigo-50 text-indigo-600 flex items-center justify-center text-xl transition-transform group-hover:scale-110 shadow-sm border border-white">
                            <i className="fas fa-flag"></i>
                        </span>
                        <div>
                            <p className="font-extrabold text-[#18216D] uppercase text-xs tracking-widest">Extracurriculars</p>
                            <p className="text-[10px] font-bold text-slate-400 mt-1">Extracurricular Governance</p>
                        </div>
                    </button>

                    <button
                        onClick={() => setShowEventModal(true)}
                        className="flex items-center space-x-6 p-6 bg-slate-50/50 border border-slate-100 rounded-[2rem] hover:border-rose-500/20 hover:bg-white hover:shadow-xl hover:shadow-rose-900/5 transition-all group text-left"
                    >
                        <span className="h-14 w-14 rounded-2xl bg-rose-50 text-rose-600 flex items-center justify-center text-xl transition-transform group-hover:scale-110 shadow-sm border border-white">
                            <i className="fas fa-calendar-check"></i>
                        </span>
                        <div>
                            <p className="font-extrabold text-[#18216D] uppercase text-xs tracking-widest">Manage Notices</p>
                            <p className="text-[10px] font-bold text-slate-400 mt-1">Deploy School-wide Alerts</p>
                        </div>
                    </button>

                    <Link
                        to="/admin/enquiries"
                        className="flex items-center space-x-6 p-6 bg-slate-50/50 border border-slate-100 rounded-[2rem] hover:border-indigo-500/20 hover:bg-white hover:shadow-xl hover:shadow-indigo-900/5 transition-all group text-left"
                    >
                        <span className="h-14 w-14 rounded-2xl bg-indigo-50 text-indigo-600 flex items-center justify-center text-xl transition-transform group-hover:scale-110 shadow-sm border border-white">
                            <i className="fas fa-comments"></i>
                        </span>
                        <div>
                            <p className="font-extrabold text-[#18216D] uppercase text-xs tracking-widest">Feedback Hub</p>
                            <p className="text-[10px] font-bold text-slate-400 mt-1">Manage Enquiries & Feedback</p>
                        </div>
                    </Link>

                    <Link
                        to="/admin/houses"
                        className="flex items-center space-x-6 p-6 bg-slate-50/50 border border-slate-100 rounded-[2rem] hover:border-[#FFC425]/20 hover:bg-white hover:shadow-xl hover:shadow-amber-900/5 transition-all group text-left"
                    >
                        <span className="h-14 w-14 rounded-2xl bg-amber-50 text-[#FFC425] flex items-center justify-center text-xl transition-transform group-hover:scale-110 shadow-sm border border-white">
                            <i className="fas fa-landmark"></i>
                        </span>
                        <div>
                            <p className="font-extrabold text-[#18216D] uppercase text-xs tracking-widest">House System</p>
                            <p className="text-[10px] font-bold text-slate-400 mt-1">Manage Identity & Governance</p>
                        </div>
                    </Link>
                </div>
            </div>

            {/* Performance Standards Registry Section */}
            <PerformanceStandards />

            {/* Modals */}
            {showUserModal && <UserRegistrationModal onClose={() => setShowUserModal(false)} />}
            {showFeeModal && <FeeFrameworkModal onClose={() => setShowFeeModal(false)} />}
            {showCycleModal && <AcademicCycleModal onClose={() => setShowCycleModal(false)} />}
            {showClubModal && <ClubModal onClose={() => setShowClubModal(false)} onSuccess={() => queryClient.invalidateQueries({ queryKey: ['admin-stats'] })} />}
            {showEventModal && <EventNoticeModal onClose={() => setShowEventModal(false)} onSuccess={() => queryClient.invalidateQueries({ queryKey: ['admin-stats'] })} />}



            {/* School-Wide Financial Ledger (Student Accounts) */}
            <SchoolFinancialLedger />
        </div>
    );
};

const AdminDashboard = () => {
    return (
        <AdminLayout>
            <Routes>
                <Route path="/" element={<Dashboard />} />
                {/* Redirect legacy dashboard route if needed */}
                <Route path="/dashboard" element={<Navigate to="/admin" replace />} />
                <Route path="/courses" element={<CourseList />} />
                <Route path="/courses/new" element={<CourseForm />} />
                <Route path="/courses/:id/edit" element={<CourseForm />} />
                <Route path="/users" element={<UserList />} />
                <Route path="/finance" element={<FeeManagement />} />
                <Route path="/finance/record-payment" element={<PaymentRecording />} />
                <Route path="/curriculum" element={<CurriculumRegistry />} />
                <Route path="/curriculum/:areaId/manage" element={<CurriculumAreaManager />} />
                <Route path="/grades" element={<GradeManagement />} />
                <Route path="/scheme-of-work" element={<SOWTrackerTab isHOS={true} />} />
                <Route path="/clubs" element={<ClubList />} />
                <Route path="/household/:studentId" element={<Household />} />
                <Route path="/events" element={<EventList />} />
                <Route path="/enquiries" element={<AdminEnquiryManager />} />
                <Route path="/houses" element={<HouseList />} />
                <Route path="/discipline" element={<DisciplineCategoryManager />} />
            </Routes>
        </AdminLayout>
    );
};

export default AdminDashboard;
